import React from 'react';

import { UserInviteByUserIdDocument } from 'src/api';
import { useNotification } from 'src/hooks/app';

import type { EmployeeInviteLinkArgs } from './useCopyEmployeeInviteLink';
import { type InviteMutationScope, useScopedInviteMutation } from './useScopedInviteMutation';

export type ResendInviteScope = InviteMutationScope;

type UseResendEmployeeInviteParams = {
  successMessage?: string;
};

/**
 * Resends the signup invite to a single pending employee using the mutation that
 * matches the caller's scope (sign-up resolves the account via SSN).
 */
export const useResendEmployeeInvite = (
  scope: ResendInviteScope,
  params: UseResendEmployeeInviteParams = {},
) => {
  const { createApolloContext } = useNotification();
  const context = createApolloContext({
    errorMessage: 'Failed to resend the invite',
    successMessage: params.successMessage ?? 'Invite resent',
  });

  const mutate = useScopedInviteMutation(scope, context);

  // Guards against rapid double-clicks firing duplicate invite emails.
  const inFlight = React.useRef(false);

  return React.useCallback(
    (employeeId: string, inviteLinkArgs?: EmployeeInviteLinkArgs | null) => {
      if (inFlight.current) return;
      inFlight.current = true;
      // Errors surface via the notification ErrorLink; catch here only so the
      // rejected promise doesn't become an unhandled rejection.
      void mutate({
        variables: {
          employeeIds: [employeeId],
          inviteScopeEntityId: scope === 'cvStaff' ? inviteLinkArgs?.entityId : undefined,
          inviteScopeParamKey: scope === 'cvStaff' ? inviteLinkArgs?.paramKey : undefined,
        },
        // The resend may have created the invite, and the actions menu builds its link from it.
        refetchQueries: [UserInviteByUserIdDocument],
      })
        .catch(() => {})
        .finally(() => {
          inFlight.current = false;
        });
    },
    [scope, mutate],
  );
};
